<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <!-- Back link -->
    <div class="mb-3">
      <router-link :to="{ name: 'pay-operations-order-amendments' }" class="oa-back-link">
        <i class="fa fa-arrow-left me-1"></i>Back to Queue
      </router-link>
    </div>

    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="text-muted mt-2">Loading bulk job...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger outline">
      <i class="fa fa-exclamation-triangle me-2"></i>{{ error }}
    </div>

    <template v-else-if="job">
      <!-- Header -->
      <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
        <div
          class="card-header bg-primary text-white d-flex justify-content-between align-items-center"
        >
          <h5 class="mb-0"><i class="fa fa-tasks me-2"></i>Bulk Job {{ job.jobId }}</h5>
          <span class="badge fs-6" :class="statusBadgeClass">{{ job.status }}</span>
        </div>
        <div class="card-body border border-primary rounded-3">
          <div class="row">
            <div class="col-md-6">
              <dl class="row mb-0 small">
                <dt class="col-sm-4">Action</dt>
                <dd class="col-sm-8">{{ job.type || job.action }}</dd>
                <dt class="col-sm-4">Actor</dt>
                <dd class="col-sm-8">{{ job.actor }}</dd>
                <dt class="col-sm-4">Reason</dt>
                <dd class="col-sm-8">{{ job.reason || '—' }}</dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row mb-0 small">
                <dt class="col-sm-4">Created</dt>
                <dd class="col-sm-8">
                  <ZonedDateTime :value="job.createdAt" :zone="userTimezone" kind="user" />
                </dd>
                <dt class="col-sm-4">Completed</dt>
                <dd class="col-sm-8">
                  <ZonedDateTime :value="job.completedAt" :zone="userTimezone" kind="user" />
                </dd>
                <dt class="col-sm-4">Total Items</dt>
                <dd class="col-sm-8">{{ job.totalItems }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary counters -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-xl-3">
          <div class="oa-stat-card text-center">
            <div class="card-body py-2">
              <h4 class="text-success mb-0">{{ job.successCount }}</h4>
              <small class="text-muted">Successful</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-3">
          <div class="oa-stat-card text-center">
            <div class="card-body py-2">
              <h4 class="text-danger mb-0">{{ job.failureCount }}</h4>
              <small class="text-muted">Failed</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-3">
          <div class="oa-stat-card text-center">
            <div class="card-body py-2">
              <h4 class="mb-0">{{ job.skipCount }}</h4>
              <small class="text-muted">Skipped</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-3">
          <div class="oa-stat-card text-center">
            <div class="card-body py-2">
              <h4 class="mb-0">
                {{ job.totalItems - job.successCount - job.failureCount - job.skipCount }}
              </h4>
              <small class="text-muted">Pending</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Items table with pagination -->
      <div class="card card-absolute rfo-card rfo-table-card rfo-card--secondary oa-results-card">
        <div class="card-header bg-secondary text-white">
          <h6 class="mb-0">Items ({{ job.items.length }} of {{ job.totalItems }})</h6>
        </div>
        <div class="card-body border border-secondary rounded-3">
          <div class="oa-card-toolbar">
            <label class="small fw-semibold mb-0" for="bulkJobStatus">Filter by status</label>
            <select
              id="bulkJobStatus"
              v-model="filterStatus"
              class="form-select border border-primary form-select-sm d-inline-block auto-width"
            >
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failure">Failed</option>
              <option value="pending">Pending</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-striped mb-0">
              <thead class="table-light">
                <tr>
                  <th>#</th>
                  <th>Order UUID</th>
                  <th>Status</th>
                  <th>Attempt ID</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in paginatedItems" :key="idx">
                  <td>{{ itemRowNumber(idx) }}</td>
                  <td class="font-monospace small">
                    <router-link
                      v-if="item.status === 'success'"
                      :to="{
                        name: 'pay-operations-order-amendment-detail',
                        params: { uuid: item.orderUuid },
                      }"
                    >
                      {{ shortUuid(item.orderUuid) }}
                    </router-link>
                    <span v-else class="font-monospace">{{ shortUuid(item.orderUuid) }}</span>
                  </td>
                  <td>
                    <span class="badge" :class="itemBadgeClass(item.status)">{{
                      item.status
                    }}</span>
                  </td>
                  <td class="font-monospace small text-muted">
                    {{ item.attemptId ? shortUuid(item.attemptId) : '—' }}
                  </td>
                  <td class="small text-danger">{{ item.error || '—' }}</td>
                </tr>
                <tr v-if="filteredItems.length === 0">
                  <td colspan="5" class="text-center text-muted py-3">
                    No items match the filter.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="lastPage > 1" class="d-flex justify-content-between align-items-center mt-3">
            <small class="text-muted">Page {{ currentPage }} of {{ lastPage }}</small>
            <div>
              <button
                class="btn btn-sm btn-outline-primary"
                :disabled="currentPage <= 1"
                @click="currentPage--"
              >
                Prev
              </button>
              <button
                class="btn btn-sm btn-outline-primary ms-1"
                :disabled="currentPage >= lastPage"
                @click="currentPage++"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Retry button -->
      <div v-if="job.failureCount > 0" class="oa-action-row">
        <button class="btn btn-warning" :disabled="isRetrying" @click="retryFailures">
          <span v-if="isRetrying"
            ><span class="spinner-border spinner-border-sm me-1"></span>Retrying...</span
          >
          <span v-else><i class="fa fa-refresh me-1"></i>Retry Failed Items</span>
        </button>
      </div>
    </template>

    <PayOpsUsageInstructions
      class="mt-4"
      description="Per-item record of a bulk operation. Every item is independently previewed, executed and audited."
      :required-roles="bulkRequiredRoles"
      :workflow-notes="bulkWorkflowNotes"
    />
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import { shortIdentifier as shortUuid } from '@/utils/display-formatters';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import {
  PAY_OPS_BULK_JOB_ROLES,
  buildBulkJobWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';

const PAGE_SIZE = 50;

export default {
  name: 'BulkJobDetail',
  components: { PayOpsUsageInstructions, ZonedDateTime },
  setup() {
    const route = useRoute();
    const toast = useToast();
    const job = ref<any>(null);
    const isLoading = ref(false);
    const isRetrying = ref(false);
    const error = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = PAGE_SIZE;
    const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);

    const jobId = route.params.id as string;

    const statusBadgeClass = computed(() => {
      switch (job.value?.status) {
        case 'completed':
          return 'bg-success';
        case 'partial':
          return 'bg-warning text-dark';
        case 'failed':
          return 'bg-danger';
        case 'running':
          return 'bg-info';
        default:
          return 'bg-secondary';
      }
    });

    function itemBadgeClass(status: string): string {
      switch (status) {
        case 'success':
          return 'bg-success';
        case 'failure':
          return 'bg-danger';
        case 'skipped':
          return 'bg-secondary';
        default:
          return 'bg-light text-dark';
      }
    }

    const filteredItems = computed(() => {
      if (!job.value) return [];
      const items = job.value.items || [];
      if (!filterStatus.value) return items;
      return items.filter((it: any) => it.status === filterStatus.value);
    });

    const lastPage = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / pageSize)));

    const paginatedItems = computed(() => {
      const start = (currentPage.value - 1) * pageSize;
      return filteredItems.value.slice(start, start + pageSize);
    });

    async function fetchJob() {
      isLoading.value = true;
      error.value = '';
      try {
        const res = await apiClient.rpIntegration.get(
          API_PATHS.rpIntegration.payOperations.orderAmendments.bulkJobById(jobId),
        );
        job.value = res.data;
      } catch (err: any) {
        error.value = err?.response?.data?.message || 'Failed to load bulk job.';
      } finally {
        isLoading.value = false;
      }
    }

    let pollHandle: ReturnType<typeof setInterval> | null = null;

    function stopPolling() {
      if (pollHandle !== null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
    }

    async function retryFailures() {
      isRetrying.value = true;
      try {
        await apiClient.rpIntegration.post(
          API_PATHS.rpIntegration.payOperations.orderAmendments.bulkJobRetry(jobId),
        );
        toast.success('Retry initiated.');
        stopPolling();
        pollHandle = setInterval(async () => {
          await fetchJob();
          if (job.value && ['completed', 'partial', 'failed'].includes(job.value.status)) {
            stopPolling();
          }
        }, 3000);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Retry failed.');
      } finally {
        isRetrying.value = false;
      }
    }

    function itemRowNumber(idx: number | string): number {
      return (currentPage.value - 1) * pageSize + Number(idx) + 1;
    }

    onMounted(fetchJob);
    onBeforeUnmount(stopPolling);

    return {
      job,
      isLoading,
      isRetrying,
      error,
      filterStatus,
      currentPage,
      pageSize,
      statusBadgeClass,
      itemBadgeClass,
      filteredItems,
      lastPage,
      paginatedItems,
      retryFailures,
      itemRowNumber,
      userTimezone,
      shortUuid,
      bulkRequiredRoles: PAY_OPS_BULK_JOB_ROLES,
      bulkWorkflowNotes: buildBulkJobWorkflowNotes(),
    };
  },
};
</script>

<style scoped>
.auto-width {
  width: auto;
}
</style>
